import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Loading from '@/widgets/Loading'
import { MANUAL_COLORS, cellKey, resolveCell, voivodeshipHeaderClass } from './boardCells'
import useWirtualnaTablica from './hooks/useWirtualnaTablica'

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

function LegendItem({ className, label, mark }) {
	return (
		<span className="inline-flex items-center gap-2 text-xs text-gray-700">
			<span className={`inline-flex h-6 min-w-8 items-center justify-center rounded border border-gray-300 px-1 font-semibold ${className}`}>
				{mark}
			</span>
			{label}
		</span>
	)
}

export default function WirtualnaTablicaPage() {
	const { t } = useTranslation()
	const {
		availabilityByCell,
		colorError,
		colors,
		days,
		error,
		handleSelectCell,
		handleSetColor,
		loading,
		month,
		projectsByCell,
		selectedCell,
		setMonth,
		setVoivodeship,
		setYear,
		visibleTrainers,
		voivodeship,
		voivodeships,
		year,
	} = useWirtualnaTablica()

	const months = t('virtualBoard.months', { returnObjects: true })
	const monthOptions = Array.isArray(months) ? months : []
	const currentYear = new Date().getFullYear()
	const years = []
	for (let value = currentYear - 2; value <= currentYear + 2; value += 1) years.push(value)

	if (loading) return <Loading />

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-semibold text-gray-900">{t('virtualBoard.title')}</h1>
				<p className="mt-1 text-sm text-gray-500">{t('virtualBoard.description')}</p>
			</div>

			{error ? (
				<div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">{error}</div>
			) : null}

			<div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:flex-row lg:items-end lg:justify-between">
				<div className="flex flex-wrap items-end gap-3">
					<label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
						{t('virtualBoard.filters.month')}
						<select
							value={month}
							onChange={event => setMonth(Number(event.target.value))}
							className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
							{monthOptions.map((label, index) => (
								<option key={label} value={index}>{label}</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
						{t('virtualBoard.filters.year')}
						<select
							value={year}
							onChange={event => setYear(Number(event.target.value))}
							className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
							{years.map(value => (
								<option key={value} value={value}>{value}</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
						{t('virtualBoard.filters.voivodeship')}
						<select
							value={voivodeship}
							onChange={event => setVoivodeship(event.target.value)}
							className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
							<option value="">{t('virtualBoard.filters.allVoivodeships')}</option>
							{voivodeships.map(item => (
								<option key={item.code} value={item.code}>{item.code} — {item.name}</option>
							))}
						</select>
					</label>
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-sm font-medium text-gray-700">{t('virtualBoard.colors.label')}</span>
					<div className="flex flex-wrap items-center gap-2">
						{MANUAL_COLORS.map(color => (
							<button
								key={color.id}
								type="button"
								disabled={!selectedCell}
								title={t(`virtualBoard.colors.${color.id}`)}
								onClick={() => handleSetColor(color.id)}
								className={`h-7 w-7 rounded border border-gray-300 ${color.swatch} ${
									selectedCell ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
								} ${colors[cellKey(selectedCell?.trainerId, selectedCell?.day)] === color.id ? 'ring-2 ring-gray-900' : ''}`}
							/>
						))}
						<button
							type="button"
							disabled={!selectedCell}
							onClick={() => handleSetColor('')}
							className={`rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-700 ${
								selectedCell ? 'hover:bg-gray-50' : 'cursor-not-allowed opacity-40'
							}`}>
							{t('virtualBoard.colors.clear')}
						</button>
					</div>
					<p className="text-xs text-gray-500">{t('virtualBoard.colors.hint')}</p>
					{colorError ? <p className="text-xs text-red-600">{colorError}</p> : null}
				</div>
			</div>

			<div className="flex flex-wrap gap-3">
				<LegendItem className="bg-emerald-400" label={t('virtualBoard.legend.available')} />
				<LegendItem className="bg-emerald-400" mark="1/0" label={t('virtualBoard.legend.morning')} />
				<LegendItem className="bg-emerald-400" mark="0/1" label={t('virtualBoard.legend.afternoon')} />
				<LegendItem className="bg-red-400" mark="x" label={t('virtualBoard.legend.weekend')} />
				<LegendItem className="bg-white" mark="x" label={t('virtualBoard.legend.unavailable')} />
				<LegendItem className="bg-white text-indigo-700" mark={t('virtualBoard.legend.projectSample')} label={t('virtualBoard.legend.project')} />
			</div>

			<div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
				<table className="min-w-max border-collapse text-xs">
					<thead>
						<tr>
							<th className="sticky left-0 top-0 z-40 w-28 border border-gray-200 bg-gray-50 px-2 py-2 text-left font-semibold text-gray-700">
								{t('virtualBoard.columns.date')}
							</th>
							{visibleTrainers.map(trainer => (
								<th
									key={`region-${trainer.id}`}
									className={`sticky top-0 z-20 min-w-32 border border-gray-200 px-2 py-2 text-center font-semibold text-gray-900 ${voivodeshipHeaderClass(trainer.voivodeship)}`}>
									{trainer.voivodeship || '—'}
								</th>
							))}
						</tr>
						<tr>
							<th className="sticky left-0 top-9 z-40 w-28 border border-gray-200 bg-gray-50 px-2 py-2 text-left font-semibold text-gray-700">
								{t('virtualBoard.columns.name')}
							</th>
							{visibleTrainers.map(trainer => (
								<th
									key={`name-${trainer.id}`}
									className="sticky top-9 z-20 min-w-32 max-w-40 border border-gray-200 bg-white px-2 py-2 text-center font-medium text-gray-900">
									{trainer.name}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{visibleTrainers.length === 0 ? (
							<tr>
								<td className="px-4 py-8 text-center text-sm text-gray-500">{t('virtualBoard.empty')}</td>
							</tr>
						) : days.map(day => (
							<tr key={day.key}>
								<th
									className={`sticky left-0 z-10 border border-gray-200 px-2 py-2 text-left font-semibold ${
										day.isWeekend ? 'bg-red-100 text-red-800' : 'bg-gray-50 text-gray-800'
									}`}>
									<div>{t(`virtualBoard.weekdays.${WEEKDAY_KEYS[day.weekday - 1]}`)}</div>
									<div>{day.day}</div>
								</th>
								{visibleTrainers.map(trainer => {
									const key = cellKey(trainer.id, day.key)
									const availability = availabilityByCell[key]
									const projects = projectsByCell[key] || []
									const cell = resolveCell({
										isWeekend: day.isWeekend,
										morning: Boolean(availability?.morning),
										afternoon: Boolean(availability?.afternoon),
										project: projects[0] || null,
										manualColor: colors[key] || '',
									})
									const isSelected = selectedCell?.trainerId === trainer.id && selectedCell?.day === day.key
									return (
										<td
											key={trainer.id}
											onClick={() => handleSelectCell(trainer.id, day.key)}
											className={`cursor-pointer border border-gray-200 px-1 py-1 text-center align-middle ${cell.className} ${
												isSelected ? 'ring-2 ring-inset ring-gray-900' : ''
											}`}>
											{cell.project ? (
												<Link
													to={`/centrumSzkolen/projekty/details/${cell.project.id}`}
													target="_blank"
													rel="noopener noreferrer"
													onClick={event => event.stopPropagation()}
													title={projects.map(item => item.name).join(', ')}
													className="inline-block max-w-36 truncate font-semibold underline">
													{cell.project.name}
												</Link>
											) : (
												<span className="font-semibold">{cell.mark}</span>
											)}
										</td>
									)
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}
